/* Nani collection page interactions
 * - Product card swatch hover
 * - Filter drawer toggle
 * - Base-color filter toggle (multi-value URL manipulation)
 * - Sort form auto-submit
 * - Quick-add drawer (fetch product, render, bundle module, ATC)
 */
(function () {
  'use strict';

  const root = document.querySelector('.nani-coll, [data-nani-card-host]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;

  // GA4 click tracking. window.naniTrack is defined in snippets/nani-tracking.liquid;
  // guard at call time since this script can run before that snippet parses.
  function track(eventName, params) {
    if (typeof window.naniTrack === 'function') window.naniTrack(eventName, params);
  }

  /* Screen-reader announcements. [data-coll-status] is a permanently-present
   * aria-live region that sits OUTSIDE [data-coll-content], so async filter
   * swaps can't destroy it mid-announcement. */
  function announce(message) {
    const region = document.querySelector('[data-coll-status]');
    if (region) region.textContent = message;
  }

  function announceResultCount() {
    const grid = root.querySelector('[data-plp-grid]');
    const count = grid ? grid.querySelectorAll('.nani-plp-grid__item:not(.is-size-oos)').length : 0;
    announce('Showing ' + count + ' product' + (count === 1 ? '' : 's'));
  }

  /* Focus trap shared by the quick-add drawer and the mobile filter drawer.
   * Cycles Tab/Shift+Tab among the container's focusable elements. Returns a
   * release function that removes the listener. */
  function trapFocus(container) {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const onKeydown = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = Array.prototype.filter.call(
        container.querySelectorAll(selector),
        (el) => el.offsetParent !== null
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  }

  /* ------------------------------------------------------------------ */
  /* Per-card interactions (swatch hover swap + slider dot indicator)   */
  /* Called once on load and again after every async filter swap.       */
  /* ------------------------------------------------------------------ */
  function initCardInteractions(scope) {
    const ctx = scope || document;

    ctx.querySelectorAll('.nani-pcard').forEach((card) => {
      if (card.__naniBound) return;
      card.__naniBound = true;
      const img = card.querySelector('[data-pcard-img]');
      const swatches = card.querySelectorAll('[data-swatch]');
      if (!img || swatches.length === 0) return;
      swatches.forEach((sw) => {
        const swap = () => {
          const url = sw.dataset.variantImage;
          if (!url) return;
          img.src = url;
          swatches.forEach((s) => s.classList.remove('is-active'));
          sw.classList.add('is-active');
        };
        sw.addEventListener('mouseenter', swap);
        sw.addEventListener('focus', swap);
      });
    });

    ctx.querySelectorAll('[data-pcard-slider]').forEach((slider) => {
      if (slider.__naniBound) return;
      slider.__naniBound = true;
      const slides = slider.querySelectorAll('.nani-pcard__slide');
      const dots = slider.parentElement && slider.parentElement.querySelector('[data-pcard-dots]');
      if (!dots || slides.length < 2) return;
      const dotEls = dots.querySelectorAll('span');
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const idx = Array.prototype.indexOf.call(slides, entry.target);
          if (idx < 0) return;
          dotEls.forEach((d, i) => d.classList.toggle('is-on', i === idx));
        });
      }, { root: slider, threshold: [0.6] });
      slides.forEach((s) => io.observe(s));
    });

    pruneSoldOutSizes();
  }

  /* ------------------------------------------------------------------ */
  /* Accurate "in stock" for size filters                               */
  /* Shopify's filter.v.availability is product-scoped: a style whose    */
  /* selected size is sold out (but other sizes in stock) still matches  */
  /* `size=X & availability=1`. So when a size filter is active and the   */
  /* shopper hasn't opted into sold-out, hide any card whose selected     */
  /* size isn't actually in stock. Each card carries data-instock-sizes   */
  /* for its OWN colorway, and the grid renders one card per colorway     */
  /* (color grouping only adds sibling swatches, it doesn't collapse      */
  /* cards) — so every card is pruned by its own stock. NOTE: if the grid */
  /* is ever changed to collapse a style into a single card, revisit this */
  /* (data-instock-sizes would then need to be the union across siblings).*/
  function pruneSoldOutSizes() {
    // Collection pages render [data-plp-grid]; the search page uses Horizon's
    // native product-grid (items still carry .nani-plp-grid__item).
    const grid = root.querySelector('[data-plp-grid]') || root.querySelector('.product-grid');
    if (!grid) return;

    const params = new URL(window.location.href).searchParams;
    const sizes = params.getAll('filter.v.option.size');
    const showSoldOut = params.get('show_sold_out') === '1';
    const active = sizes.length > 0 && !showSoldOut;

    const items = grid.querySelectorAll('.nani-plp-grid__item');
    let hidden = 0;
    items.forEach((item) => {
      const card = item.querySelector('.nani-pcard');
      if (!card) return;
      if (!active) {
        item.classList.remove('is-size-oos');
        return;
      }
      const inStock = (card.dataset.instockSizes || '').split('|').filter(Boolean);
      const keep = sizes.some((s) => inStock.includes(s));
      item.classList.toggle('is-size-oos', !keep);
      if (!keep) hidden++;
    });

    toggleSizeEmpty(active && items.length > 0 && hidden === items.length);
    syncVisibleCounts();
  }

  /* Keep the "Showing X" progress line honest once cards are pruned. The server
   * count (paginate.items) includes the availability false-positives we hide, and
   * the true cross-page total isn't knowable client-side — so when the prune is
   * active we show the count of actually-visible styles, with a "+" when more
   * pages remain. When the prune is inactive the server-rendered text stands. */
  function syncVisibleCounts() {
    const grid = root.querySelector('[data-plp-grid]');
    if (!grid) return;
    const params = new URL(window.location.href).searchParams;
    const active = params.getAll('filter.v.option.size').length > 0 && params.get('show_sold_out') !== '1';
    if (!active) return;

    const visible = grid.querySelectorAll('.nani-plp-grid__item:not(.is-size-oos)').length;
    const more = !!root.querySelector('[data-load-more]');
    const text = 'Showing ' + visible + (more ? '+' : '') + ' in stock';

    const lm = root.querySelector('[data-load-more-progress]');
    if (lm) lm.textContent = text;
  }

  function toggleSizeEmpty(show) {
    const wrap = root.querySelector('.nani-coll__grid-wrap');
    if (!wrap) return;
    let el = wrap.querySelector('[data-size-oos-empty]');
    if (show) {
      if (!el) {
        el = document.createElement('div');
        el.className = 'nani-plp-empty';
        el.setAttribute('data-size-oos-empty', '');
        el.innerHTML = '<h2>Sold out in this size.</h2>' +
          '<p>No styles have the selected size in stock right now. Try another size, or include sold-out styles.</p>';
        wrap.appendChild(el);
      }
      el.hidden = false;
    } else if (el) {
      el.hidden = true;
    }
  }

  initCardInteractions();

  /* Search page: filtering is driven by Horizon's facets.js, which morphs the
   * results-list in place (no [data-coll-content] swap of ours). Re-bind card
   * interactions and re-run the in-stock size prune after each morph. Only
   * childList is observed, so the prune's own class toggles can't re-trigger it. */
  if (!root.querySelector('[data-coll-content]')) {
    let rebindTimer;
    new MutationObserver(() => {
      clearTimeout(rebindTimer);
      rebindTimer = setTimeout(() => initCardInteractions(root), 60);
    }).observe(root, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* Async filter: fetch the section via the Section Rendering API     */
  /* and swap [data-coll-content] without a page reload.                */
  /* ------------------------------------------------------------------ */
  let fetchAbort = null;

  /* In-stock by default: the size (and colour/price) option filters match on
   * variant *existence*, not stock, so with a catalog where nearly every style
   * carries the full S–XXL run they barely narrow anything. Forcing
   * `filter.v.availability=1` makes Shopify intersect at the variant level, so
   * a size filter returns only styles that have that size in stock. The
   * "Include sold out" toggle drops the constraint; its state rides in the URL
   * as `show_sold_out=1` so it survives async swaps, pagination and reloads. */
  function normalizeAvailability(url) {
    const showSoldOut = url.searchParams.get('show_sold_out') === '1';
    url.searchParams.delete('filter.v.availability');
    if (!showSoldOut) url.searchParams.set('filter.v.availability', '1');
    return url;
  }

  function syncInstockToggle(url) {
    const toggle = root.querySelector('[data-instock-toggle]');
    if (toggle) toggle.checked = url.searchParams.get('show_sold_out') === '1';
  }

  function applyUrl(targetUrl, opts) {
    const url = new URL(targetUrl, window.location.origin);
    normalizeAvailability(url);
    const fetchUrl = new URL(url.toString());
    if (sectionId) fetchUrl.searchParams.set('section_id', sectionId);

    const content = root.querySelector('[data-coll-content]');
    if (!content || !sectionId) {
      // Fall back to a hard navigation if we can't async-swap
      window.location.href = url.toString();
      return;
    }

    if (fetchAbort) fetchAbort.abort();
    fetchAbort = new AbortController();
    content.classList.add('is-loading');

    fetch(fetchUrl.toString(), { signal: fetchAbort.signal, headers: { 'X-Requested-With': 'fetch' } })
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('[data-coll-content]');
        if (!incoming) {
          window.location.href = url.toString();
          return;
        }
        content.innerHTML = incoming.innerHTML;
        content.classList.remove('is-loading');

        // Reset the URL bar (without the section_id param)
        const publicUrl = url.pathname + (url.search ? url.search : '') + url.hash;
        history.pushState({}, '', publicUrl);

        // Keep the (possibly re-rendered) "Include sold out" toggle in sync
        syncInstockToggle(url);

        // Re-attach per-card interactions to the new markup
        initCardInteractions(content);

        // The swap rebuilt the filter drawer — re-assert its open/closed state
        // (and body scroll lock) so ticking a filter doesn't freeze the page.
        restoreFilterState();

        // Announce the new result count to screen readers.
        announceResultCount();

        // Scroll the toolbar into view so the user sees results updating. Skip
        // while the drawer is open (results sit behind it and body is locked).
        if ((!opts || opts.scroll !== false) && !filtersOpen) {
          const toolbar = content.querySelector('[data-toolbar]');
          if (toolbar) {
            const top = toolbar.getBoundingClientRect().top + window.pageYOffset - 80;
            if (window.pageYOffset > top) window.scrollTo({ top, behavior: 'smooth' });
          }
        }
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        content.classList.remove('is-loading');
        window.location.href = url.toString();
      });
  }

  // Back/forward navigation should also async-swap
  window.addEventListener('popstate', () => {
    applyUrl(window.location.href, { scroll: false });
  });

  /* ------------------------------------------------------------------ */
  /* Load more: append the next page's cards without a reload.           */
  /* Delegated so it keeps working after filter/sort re-renders the grid.*/
  /* ------------------------------------------------------------------ */
  let loadMoreAbort = null;

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    // The control is a real <a href> so crawlers can follow ?page=N; JS users
    // get the append-in-place behavior instead.
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;

    const nextUrl = btn.getAttribute('href') || btn.dataset.nextUrl;
    const grid = root.querySelector('[data-plp-grid]');
    if (!nextUrl || !grid || !sectionId) return;

    // Keep the un-normalized page URL for the address bar; normalize a copy
    // (availability constraint) for the actual fetch.
    const pageUrl = new URL(nextUrl, window.location.origin);
    const url = normalizeAvailability(new URL(pageUrl.toString()));
    const fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', sectionId);

    if (loadMoreAbort) loadMoreAbort.abort();
    loadMoreAbort = new AbortController();
    btn.setAttribute('aria-busy', 'true');
    const label = btn.textContent;
    btn.textContent = 'Loading…';

    fetch(fetchUrl.toString(), { signal: loadMoreAbort.signal, headers: { 'X-Requested-With': 'fetch' } })
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = doc.querySelector('[data-plp-grid]');
        if (!incomingGrid) {
          window.location.href = url.toString();
          return;
        }

        const newItems = Array.prototype.slice.call(incomingGrid.children);
        newItems.forEach((item) => grid.appendChild(item));
        initCardInteractions(grid);

        // Advance or retire the control based on the incoming page's own control.
        const incomingBtn = doc.querySelector('[data-load-more]');
        const incomingNext = incomingBtn && (incomingBtn.getAttribute('href') || incomingBtn.dataset.nextUrl);
        if (incomingNext) {
          btn.setAttribute('href', incomingNext);
          btn.setAttribute('aria-busy', 'false');
          btn.textContent = label;
        } else {
          btn.remove();
        }

        // Reflect the loaded page in the address bar (?page=N, other params kept)
        // so reload/share/back lands on the same depth.
        history.replaceState({}, '', pageUrl.pathname + pageUrl.search + pageUrl.hash);

        // Update the "Showing X of Y" progress line with the cumulative count.
        const progress = root.querySelector('[data-load-more-progress]');
        const total = parseInt(btn.dataset.total, 10);
        if (progress) {
          const shown = grid.children.length;
          progress.textContent = 'Showing ' + shown + (total ? ' of ' + total : '');
        }
        // Override with the in-stock-visible count when the size prune is active.
        syncVisibleCounts();
        // Announce the new result count to screen readers.
        announceResultCount();
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        btn.setAttribute('aria-busy', 'false');
        btn.textContent = label;
        window.location.href = url.toString();
      });
  });

  /* ------------------------------------------------------------------ */
  /* Filter drawer (mobile) + auto-submit checkboxes                    */
  /* ------------------------------------------------------------------ */
  /* The filter drawer ([data-filters]), its overlay, and the toggle button all
   * live INSIDE [data-coll-content], so every async filter swap replaces them
   * with fresh nodes. So we hold NO element references: query fresh on each
   * call, delegate all clicks on `root`, and track the open state in a variable
   * so it survives the swap. Without this the drawer is rebuilt closed on every
   * filter tick while `body { overflow:hidden }` stays set — the page ends up
   * permanently scroll-locked (looks like a full freeze). */
  const isMobileViewport = () => window.matchMedia('(max-width: 989px)').matches;
  const getFilterPanel = () => root.querySelector('[data-filters]');
  const getFilterOverlay = () => root.querySelector('[data-filter-overlay]');
  let filtersOpen = false;
  let filterCloseTimer;
  let filtersReleaseTrap = null;
  let filtersWasDialog = false;

  /* The same <aside data-filters> renders as an inline sidebar on desktop, so
   * dialog semantics (role/aria-modal) and the focus trap only apply while it's
   * acting as the mobile drawer — set on open, removed on close. */
  function setFilterDialogState(panel, on) {
    if (filtersReleaseTrap) { filtersReleaseTrap(); filtersReleaseTrap = null; }
    if (!panel) return;
    if (on) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      filtersReleaseTrap = trapFocus(panel);
    } else {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }
  }

  function openFilters() {
    const panel = getFilterPanel();
    if (!panel) return;
    clearTimeout(filterCloseTimer);
    filtersOpen = true;
    const overlay = getFilterOverlay();
    panel.removeAttribute('hidden');
    panel.classList.add('is-open');
    if (overlay) { overlay.removeAttribute('hidden'); overlay.classList.add('is-open'); }
    const toggle = root.querySelector('[data-filter-toggle]');
    toggle && toggle.setAttribute('aria-expanded', 'true');
    if (isMobileViewport()) {
      document.body.style.overflow = 'hidden';
      setFilterDialogState(panel, true);
      filtersWasDialog = true;
      const closeBtn = panel.querySelector('[data-filter-close]');
      closeBtn && closeBtn.focus();
    }
  }
  function closeFilters() {
    filtersOpen = false;
    const panel = getFilterPanel();
    const overlay = getFilterOverlay();
    panel && panel.classList.remove('is-open');
    overlay && overlay.classList.remove('is-open');
    setFilterDialogState(panel, false);
    const toggle = root.querySelector('[data-filter-toggle]');
    toggle && toggle.setAttribute('aria-expanded', 'false');
    if (filtersWasDialog) {
      filtersWasDialog = false;
      toggle && toggle.focus();
    }
    document.body.style.overflow = '';
    clearTimeout(filterCloseTimer);
    filterCloseTimer = setTimeout(() => {
      if (isMobileViewport()) {
        const p = getFilterPanel();
        const o = getFilterOverlay();
        p && p.setAttribute('hidden', '');
        o && o.setAttribute('hidden', '');
      }
    }, 280);
  }

  // Escape closes the mobile filter drawer (the quick-add drawer has its own
  // Escape handling and stacks above the filters, so it takes precedence).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !filtersOpen) return;
    const qa = document.querySelector('[data-qa-drawer]');
    if (qa && qa.classList.contains('is-open')) return;
    closeFilters();
  });

  /* Re-assert the drawer's open/closed state on the freshly-swapped markup after
   * a filter change so it doesn't strand the page with the drawer gone but body
   * scroll still locked. Desktop always shows the sidebar and never scroll-locks. */
  function restoreFilterState() {
    const panel = getFilterPanel();
    if (!panel) return;
    if (!isMobileViewport()) {
      panel.removeAttribute('hidden');
      document.body.style.overflow = '';
      return;
    }
    const toggle = root.querySelector('[data-filter-toggle]');
    if (filtersOpen) {
      const overlay = getFilterOverlay();
      panel.removeAttribute('hidden');
      panel.classList.add('is-open');
      if (overlay) { overlay.removeAttribute('hidden'); overlay.classList.add('is-open'); }
      toggle && toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      // The swap replaced the panel node — re-apply dialog semantics + trap.
      setFilterDialogState(panel, true);
    } else {
      toggle && toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }

  // Delegate toggle + close + overlay clicks on `root` so they keep working
  // after a swap replaces the drawer markup (and regardless of whether the
  // click lands on an SVG inside the button).
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-toggle]')) {
      e.preventDefault();
      if (filtersOpen) closeFilters();
      else openFilters();
      return;
    }
    if (e.target.closest('[data-filter-close]')) {
      e.preventDefault();
      e.stopPropagation();
      closeFilters();
      return;
    }
    if (e.target.closest('[data-filter-overlay]')) {
      closeFilters();
    }
  });

  // Reflect the correct initial state (desktop sidebar shown; mobile closed).
  restoreFilterState();

  /* ------------------------------------------------------------------ */
  /* Filter / sort / base-color / chips → async swap via applyUrl()     */
  /* All listeners are delegated on `root` so they survive the swap.    */
  /* ------------------------------------------------------------------ */
  function buildFilterUrl() {
    const form = root.querySelector('[data-filter-form]');
    const params = new URLSearchParams();

    // The Color filter renders as base-color buttons ([data-base-color]), not
    // form inputs — its active state lives only in the URL. Seed any filter.*
    // param that has no matching form input (notably filter.v.option.color)
    // from the current URL first, so rebuilding from form inputs doesn't
    // silently drop it when the user toggles a size/price/list filter.
    // filter.v.availability is owned by normalizeAvailability(); sort_by and
    // show_sold_out are re-added below; page is intentionally dropped so
    // changing a filter resets to page 1.
    const formOwned = new Set();
    if (form) form.querySelectorAll('input').forEach((input) => formOwned.add(input.name));
    new URL(window.location.href).searchParams.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (key === 'filter.v.availability') return;
      if (formOwned.has(key)) return;
      params.append(key, value);
    });

    if (form) {
      form.querySelectorAll('input').forEach((input) => {
        if (input.dataset.instockToggle) return; // handled via show_sold_out below
        if (input.type === 'checkbox' && input.checked) {
          params.append(input.name, input.value);
        } else if (input.type === 'number' && input.value !== '') {
          params.append(input.name, input.value);
        }
      });
    }
    const sortSelect = root.querySelector('[data-sort-select]');
    if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);
    const toggle = root.querySelector('[data-instock-toggle]');
    if (toggle && toggle.checked) params.set('show_sold_out', '1');
    return window.location.pathname + (params.toString() ? '?' + params.toString() : '');
  }

  let priceDebounce;
  root.addEventListener('change', (e) => {
    if (e.target.matches('[data-instock-toggle]')) {
      applyUrl(buildFilterUrl());
    } else if (e.target.matches('input[type="checkbox"][data-filter-input]')) {
      if (e.target.name === 'filter.v.option.size') {
        track('collection_filter_size', {
          filter_value: e.target.value,
          filter_action: e.target.checked ? 'apply' : 'remove',
          collection: window.location.pathname,
        });
      }
      applyUrl(buildFilterUrl());
    } else if (e.target.matches('[data-sort-select]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      applyUrl(url.toString());
    }
  });
  root.addEventListener('input', (e) => {
    if (e.target.matches('[data-filter-form] input[type="number"]')) {
      clearTimeout(priceDebounce);
      priceDebounce = setTimeout(() => applyUrl(buildFilterUrl()), 600);
    }
  });

  root.addEventListener('click', (e) => {
    // Base-color swatch button
    const baseBtn = e.target.closest('[data-base-color]');
    if (baseBtn) {
      e.preventDefault();
      const param = baseBtn.dataset.param;
      const values = (baseBtn.dataset.values || '').split('|').filter(Boolean);
      if (!param || values.length === 0) return;
      const url = new URL(window.location.href);
      const current = url.searchParams.getAll(param);
      const anyActive = values.some((v) => current.includes(v));
      track('collection_filter_color', {
        filter_value: baseBtn.dataset.baseName || values[0],
        filter_action: anyActive ? 'remove' : 'apply',
        collection: window.location.pathname,
      });
      url.searchParams.delete(param);
      current.filter((v) => !values.includes(v)).forEach((v) => url.searchParams.append(param, v));
      if (!anyActive) values.forEach((v) => url.searchParams.append(param, v));
      applyUrl(url.toString());
      return;
    }

    // Applied filter chip (remove) and "Clear all" reset to the in-stock default.
    const link = e.target.closest('.nani-applied-chip, .nani-applied-clear, [data-filter-clear]');
    if (link && link.href) {
      e.preventDefault();
      applyUrl(link.href);
    }
  });

  /* On load: reflect the sold-out choice in the toggle, and enforce the
   * in-stock default for deep-linked / shared filtered URLs. The bare
   * collection page is left untouched (no extra section fetch) since only
   * fully-sold-out styles differ there and in-stock kicks in on first filter. */
  (function initInstockDefault() {
    const toggle = root.querySelector('[data-instock-toggle]');
    const u = new URL(window.location.href);
    if (toggle) toggle.checked = u.searchParams.get('show_sold_out') === '1';
    const hasActiveFilter = [...u.searchParams.keys()].some(
      (k) => k.startsWith('filter.') && k !== 'filter.v.availability'
    );
    if (
      hasActiveFilter &&
      u.searchParams.get('show_sold_out') !== '1' &&
      !u.searchParams.has('filter.v.availability')
    ) {
      applyUrl(window.location.href, { scroll: false });
    }
  })();

  /* ------------------------------------------------------------------ */
  /* Quick-add drawer                                                   */
  /* ------------------------------------------------------------------ */
  const drawer = document.querySelector('[data-qa-drawer]');
  const drawerOverlay = document.querySelector('[data-qa-overlay]');
  if (!drawer) return;

  const drawerContent = drawer.querySelector('[data-qa-content]');
  const drawerLoader = drawer.querySelector('[data-qa-loader]');
  const drawerFoot = drawer.querySelector('[data-qa-foot]');
  const drawerCloses = drawer.querySelectorAll('[data-qa-close]');

  const cfgEl = document.querySelector('[data-qa-config]');
  const config = cfgEl ? JSON.parse(cfgEl.textContent) : { discount: 0, threshold: 75, moneyFormat: '${{amount}}' };

  // Bundle promo master switch, driven by theme settings (Bundle promo → "Advertise the
  // bundle (pair & save) discount") via snippets/nani-quick-add-drawer.liquid. When the
  // automatic discount is paused in admin the snippet emits discount: 0, and the pair
  // module degrades to a plain cross-sell — no savings copy, no struck prices, no
  // discounted totals, and no _bundle line-item property.
  const bundlePromoOn = (config.discount || 0) > 0;

  // Presale detection — tag list comes from theme settings (Presale / Pre-order);
  // the attribute is only emitted when "hide ships-in-24h notes" is enabled.
  const presaleTags = (drawer.getAttribute('data-presale-tags') || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  function isPresale(p) {
    if (!presaleTags.length || !p || !Array.isArray(p.tags)) return false;
    return p.tags.some((t) => presaleTags.includes(String(t).trim().toLowerCase()));
  }

  // Pair products come from each parent's `custom.complete_the_fit_products`
  // metafield. Handles are emitted on each trigger as `data-pair-handles`
  // (comma-separated); the actual product JSON is fetched lazily on drawer open
  // to keep the PLP HTML payload small.
  const pairsCache = new Map(); // handle → product.js JSON
  let pairsData = []; // pairs for the currently-open product

  function formatMoney(cents) {
    const n = (cents / 100).toFixed(2);
    if (config.moneyFormat) return config.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, n).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    return '$' + n;
  }

  let state = {
    product: null,
    variantIdx: 0,
    size: null,
    rating: null,
    ratingCount: null,
    bundlePartner: null,
    bundleVariantIdx: 0,
    bundleSize: null,
  };

  function findOptionIdx(product, kind) {
    if (!product || !product.options) return -1;
    return product.options.findIndex((o) => (o.name || o).toLowerCase().includes(kind));
  }

  function getOptionValues(product, optionIdx) {
    if (optionIdx < 0) return [];
    const seen = new Set();
    const out = [];
    product.variants.forEach((v) => {
      const val = v.options[optionIdx];
      if (!seen.has(val)) {
        seen.add(val);
        out.push({
          name: val,
          variant: v,
          available: v.available,
          image: (v.featured_image && v.featured_image.src) || v.image || product.featured_image || product.image || (product.images && product.images[0]) || null,
        });
      }
    });
    return out;
  }

  function findVariant(product, options) {
    return product.variants.find((v) =>
      options.every((opt, i) => opt == null || v.options[i] === opt)
    );
  }

  let drawerFocusReturn = null;
  let drawerReleaseTrap = null;

  function openDrawer() {
    drawerFocusReturn = document.activeElement;
    drawer.removeAttribute('hidden');
    drawerOverlay && drawerOverlay.removeAttribute('hidden');
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawerOverlay && drawerOverlay.classList.add('is-open');
    });
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (!drawerReleaseTrap) drawerReleaseTrap = trapFocus(drawer);
    const closeBtn = drawer.querySelector('[data-qa-close]');
    closeBtn && closeBtn.focus();
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawerOverlay && drawerOverlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (drawerReleaseTrap) { drawerReleaseTrap(); drawerReleaseTrap = null; }
    if (drawerFocusReturn && document.contains(drawerFocusReturn)) drawerFocusReturn.focus();
    drawerFocusReturn = null;
    setTimeout(() => {
      drawer.setAttribute('hidden', '');
      drawerOverlay && drawerOverlay.setAttribute('hidden', '');
    }, 320);
  }

  drawerCloses.forEach((b) => b.addEventListener('click', closeDrawer));
  drawerOverlay && drawerOverlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    e.preventDefault();
    const handle = trigger.dataset.productHandle;
    if (!handle) return;
    track('quick_add_open', {
      product_handle: handle,
      collection: window.location.pathname,
    });
    const rating = trigger.dataset.rating ? parseFloat(trigger.dataset.rating) : null;
    const ratingCount = trigger.dataset.ratingCount ? parseInt(trigger.dataset.ratingCount, 10) : null;
    const pairHandles = (trigger.dataset.pairHandles || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    fetchAndOpen(handle, { rating, ratingCount, pairHandles });
  });

  function fetchProduct(handle) {
    if (pairsCache.has(handle)) return Promise.resolve(pairsCache.get(handle));
    return fetch('/products/' + handle + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return null;
        // Normalize a top-level `image` for chip/preview rendering.
        const fi = p.featured_image;
        if (typeof fi === 'string') p.image = fi;
        else if (fi && fi.src) p.image = fi.src;
        else if (p.images && p.images[0]) p.image = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src;
        pairsCache.set(handle, p);
        return p;
      })
      .catch(() => null);
  }

  function fetchAndOpen(handle, meta) {
    state = {
      product: null, variantIdx: 0, size: null,
      rating: meta && meta.rating != null ? meta.rating : null,
      ratingCount: meta && meta.ratingCount != null ? meta.ratingCount : null,
      bundlePartner: null, bundleVariantIdx: 0, bundleSize: null,
    };
    pairsData = [];
    drawerContent.setAttribute('hidden', '');
    drawerLoader && drawerLoader.removeAttribute('hidden');
    drawerFoot && drawerFoot.setAttribute('hidden', '');
    openDrawer();

    const pairHandles = (meta && meta.pairHandles) || [];

    Promise.all([
      fetchProduct(handle),
      Promise.all(pairHandles.map(fetchProduct)),
    ])
      .then(([product, pairs]) => {
        if (!product) throw new Error('product fetch failed');
        state.product = product;
        state.variantIdx = 0;
        pairsData = pairs.filter(Boolean);
        if (pairsData.length > 0) {
          state.bundlePartner = pairsData[0];
          state.bundleVariantIdx = 0;
        }
        render();
      })
      .catch(() => {
        drawerContent.innerHTML = '<p style="padding:24px;text-align:center;color:rgb(var(--color-foreground-rgb)/0.7)">Could not load this product. Please try again.</p>';
        drawerContent.removeAttribute('hidden');
        drawerLoader && drawerLoader.setAttribute('hidden', '');
      });
  }

  function syncBundleSizeToPrimary() {
    if (!state.size || !state.bundlePartner) return;
    const partner = state.bundlePartner;
    const partnerColorIdx = findOptionIdx(partner, 'color');
    const partnerSizeIdx = findOptionIdx(partner, 'size');
    if (partnerSizeIdx < 0) return;
    const partnerColors = getOptionValues(partner, partnerColorIdx);
    const partnerActiveColor = partnerColors[state.bundleVariantIdx] || partnerColors[0];
    const match = partner.variants.find((v) => {
      const colorMatch = partnerColorIdx < 0 || (partnerActiveColor && v.options[partnerColorIdx] === partnerActiveColor.name);
      return colorMatch && v.options[partnerSizeIdx] === state.size && v.available;
    });
    if (match) state.bundleSize = state.size;
  }

  function render() {
    if (!state.product) return;
    drawerLoader && drawerLoader.setAttribute('hidden', '');
    drawerContent.removeAttribute('hidden');
    drawerFoot && drawerFoot.removeAttribute('hidden');

    const p = state.product;
    const colorIdx = findOptionIdx(p, 'color');
    const sizeIdx = findOptionIdx(p, 'size');
    const colors = getOptionValues(p, colorIdx);
    const activeColor = colors[state.variantIdx] || colors[0];
    const activeImage = (activeColor && activeColor.image) || (p.featured_image) || (p.images && p.images[0]);

    // Build size availability for currently selected color
    let sizes = [];
    if (sizeIdx >= 0) {
      const seen = new Set();
      p.variants.forEach((v) => {
        const colorMatch = colorIdx < 0 || (activeColor && v.options[colorIdx] === activeColor.name);
        if (!colorMatch) return;
        const sz = v.options[sizeIdx];
        if (!seen.has(sz)) {
          seen.add(sz);
          sizes.push({ label: sz, variant: v, available: v.available });
        }
      });
    }

    const bundleEnabled = pairsData.length > 0;
    const partner = state.bundlePartner;
    let partnerColorIdx = -1, partnerSizeIdx = -1, partnerColors = [], partnerActiveColor = null, partnerSizes = [];
    if (partner) {
      partnerColorIdx = findOptionIdx(partner, 'color');
      partnerSizeIdx = findOptionIdx(partner, 'size');
      partnerColors = getOptionValues(partner, partnerColorIdx);
      partnerActiveColor = partnerColors[state.bundleVariantIdx] || partnerColors[0];
      if (partnerSizeIdx >= 0) {
        const seen = new Set();
        partner.variants.forEach((v) => {
          const colorMatch = partnerColorIdx < 0 || (partnerActiveColor && v.options[partnerColorIdx] === partnerActiveColor.name);
          if (!colorMatch) return;
          const sz = v.options[partnerSizeIdx];
          if (!seen.has(sz)) {
            seen.add(sz);
            partnerSizes.push({ label: sz, variant: v, available: v.available });
          }
        });
      }
    }

    drawerContent.innerHTML = `
      <div class="nani-qa__product">
        <div class="nani-qa__product-img">${activeImage ? `<img src="${activeImage}" alt="${escapeHtml(p.title)}">` : ''}</div>
        <div class="nani-qa__product-info">
          <h3>${escapeHtml(p.title)}</h3>
          ${renderRating(p)}
          ${activeColor ? `<div class="nani-qa__product-color">Color: <b>${escapeHtml(activeColor.name)}</b></div>` : ''}
          <div class="nani-qa__product-price">${formatMoney(p.price)}</div>
        </div>
      </div>

      ${colors.length > 1 ? `
        <div class="nani-qa__section">
          <div class="nani-qa__section-head"><span>Color · ${colors.length} options</span></div>
          <div class="nani-qa__swatches">
            ${colors.map((c, i) => `
              <button type="button" class="nani-qa__swatch${i === state.variantIdx ? ' is-active' : ''}" data-set-color="${i}" aria-pressed="${i === state.variantIdx ? 'true' : 'false'}" aria-label="${escapeHtml(c.name)}">
                <span class="nani-qa__swatch-img">${c.image ? `<img src="${c.image}" alt="">` : ''}</span>
                <span class="nani-qa__swatch-name">${escapeHtml(c.name)}</span>
              </button>
            `).join('')}
          </div>
        </div>` : ''}

      ${sizes.length ? `
        <div class="nani-qa__section">
          <div class="nani-qa__section-head"><span>Size</span><a class="nani-qa__helper" href="#size-guide">Size guide →</a></div>
          <div class="nani-qa__sizes">
            ${sizes.map((s) => `
              <button type="button" class="nani-qa__size${state.size === s.label ? ' is-on' : ''}${!s.available ? ' is-out' : ''}"
                      data-set-size="${escapeHtml(s.label)}"
                      aria-pressed="${state.size === s.label ? 'true' : 'false'}"
                      ${!s.available ? 'disabled' : ''}>
                ${escapeHtml(s.label)}
              </button>
            `).join('')}
          </div>
          <div class="nani-qa__stockline">
            ${state.size
              ? `<span class="dot"></span><span>In stock${isPresale(p) ? '' : ' · ships in 24h from Utah'}</span>`
              : '<span style="color:rgb(var(--color-foreground-rgb)/0.55)">Pick a size to continue</span>'}
          </div>
        </div>` : ''}

      ${bundleEnabled ? renderBundle(p, activeImage, activeColor, partner, partnerActiveColor, partnerColors, partnerSizes) : ''}
    `;

    // Wire up interactions inside the drawer
    drawerContent.querySelectorAll('[data-set-color]').forEach((b) => {
      b.addEventListener('click', () => {
        state.variantIdx = parseInt(b.dataset.setColor, 10);
        state.size = null;
        render();
      });
    });
    drawerContent.querySelectorAll('[data-set-size]').forEach((b) => {
      b.addEventListener('click', () => {
        state.size = b.dataset.setSize;
        syncBundleSizeToPrimary();
        render();
      });
    });
    drawerContent.querySelectorAll('[data-set-bundle-color]').forEach((b) => {
      b.addEventListener('click', () => {
        state.bundleVariantIdx = parseInt(b.dataset.setBundleColor, 10);
        state.bundleSize = null;
        syncBundleSizeToPrimary();
        render();
      });
    });
    drawerContent.querySelectorAll('[data-set-bundle-size]').forEach((b) => {
      b.addEventListener('click', () => {
        state.bundleSize = b.dataset.setBundleSize;
        render();
      });
    });
    drawerContent.querySelectorAll('[data-set-partner]').forEach((b) => {
      b.addEventListener('click', () => {
        const idx = parseInt(b.dataset.setPartner, 10);
        state.bundlePartner = pairsData[idx];
        state.bundleVariantIdx = 0;
        state.bundleSize = null;
        syncBundleSizeToPrimary();
        render();
      });
    });
    const removePartner = drawerContent.querySelector('[data-remove-partner]');
    if (removePartner) {
      removePartner.addEventListener('click', () => {
        state.bundlePartner = null;
        state.bundleSize = null;
        render();
      });
    }
    const addPartner = drawerContent.querySelector('[data-add-partner]');
    if (addPartner) {
      addPartner.addEventListener('click', () => {
        if (pairsData.length > 0) {
          state.bundlePartner = pairsData[0];
          state.bundleVariantIdx = 0;
          state.bundleSize = null;
          syncBundleSizeToPrimary();
          render();
        }
      });
    }

    renderFooter(p, partner);
  }

  function renderBundle(top, topImage, topColor, partner, partnerColor, partnerColors, partnerSizes) {
    return `
      <div class="nani-qa__bundle">
        <span class="nani-qa__bundle-eyebrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="12 2 15 8 22 9 17 14 18 21 12 17 6 21 7 14 2 9 9 8 12 2"/></svg>
          ${bundlePromoOn ? 'Pair &amp; Save' : 'Pair it'}
        </span>
        <div class="nani-qa__bundle-title">Make it a <em>matching set</em>.</div>
        <div class="nani-qa__bundle-sub">${bundlePromoOn
          ? `Add a bottom and we'll take <b>${config.discount}% off the pair</b> — auto-applied at checkout. Free returns on the whole set.`
          : `Add the bottom that goes with it. Free returns on the whole set.`}</div>

        <div class="nani-qa__bundle-pair">
          <div class="nani-qa__bundle-card is-current">
            <span class="nani-qa__bundle-card-tag">Your top</span>
            <div class="nani-qa__bundle-card-img">${topImage ? `<img src="${topImage}" alt="">` : ''}</div>
            <div class="nani-qa__bundle-card-name">${escapeHtml(top.title)}</div>
            <div class="nani-qa__bundle-card-color">${topColor ? escapeHtml(topColor.name) : ''} · ${state.size || 'Pick size'}</div>
            <div class="nani-qa__bundle-card-price">${formatMoney(top.price)}</div>
          </div>

          <div class="nani-qa__bundle-plus" aria-hidden="true">+</div>

          ${partner ? `
            <div class="nani-qa__bundle-card">
              <span class="nani-qa__bundle-card-tag" style="background:rgb(var(--color-foreground-rgb)/0.08);color:var(--color-foreground)">+ bottom</span>
              <div class="nani-qa__bundle-card-img">${partnerColor && partnerColor.image ? `<img src="${partnerColor.image}" alt="">` : ''}</div>
              <div class="nani-qa__bundle-card-name">${escapeHtml(partner.title)}</div>
              <div class="nani-qa__bundle-card-color">${partnerColor ? escapeHtml(partnerColor.name) : ''} · ${state.bundleSize || 'Pick size'}</div>
              <div class="nani-qa__bundle-card-price">${formatMoney(partner.price)}</div>
              <button type="button" class="nani-qa__bundle-card-cta" data-remove-partner aria-label="Remove bundle partner">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
              </button>
            </div>
          ` : `
            <button type="button" class="nani-qa__bundle-card is-empty" data-add-partner>
              <span class="plus"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="6" x2="12" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/></svg></span>
              <span class="label">Add a bottom</span>
              <span class="hint">${bundlePromoOn ? `Save ${config.discount}% on the set` : 'Complete the set'}</span>
            </button>
          `}
        </div>

        ${pairsData.length > 0 ? `
          <div class="nani-qa__bundle-suggestions">
            <div class="nani-qa__bundle-suggestions-head">Customers paired this with</div>
            <div class="nani-qa__bundle-chips">
              ${pairsData.map((p, i) => `
                <button type="button" class="nani-qa__bundle-chip${partner && partner.id === p.id ? ' is-on' : ''}" data-set-partner="${i}" aria-pressed="${partner && partner.id === p.id ? 'true' : 'false'}">
                  <span class="nani-qa__bundle-chip-img">${p.image ? `<img src="${p.image}" alt="">` : ''}</span>
                  <span class="nani-qa__bundle-chip-body">
                    <span class="nani-qa__bundle-chip-name">${escapeHtml(p.title)}</span>
                    <span class="nani-qa__bundle-chip-price">${formatMoney(p.price)}</span>
                  </span>
                </button>
              `).join('')}
            </div>
          </div>` : ''}

        ${partner && partnerColors.length > 1 ? `
          <div class="nani-qa__bundle-sizing-head">${escapeHtml(partner.title)} · color</div>
          <div class="nani-qa__swatches" style="margin-bottom:14px">
            ${partnerColors.map((c, i) => `
              <button type="button" class="nani-qa__swatch${i === state.bundleVariantIdx ? ' is-active' : ''}" data-set-bundle-color="${i}" aria-pressed="${i === state.bundleVariantIdx ? 'true' : 'false'}" aria-label="${escapeHtml(c.name)}">
                <span class="nani-qa__swatch-img">${c.image ? `<img src="${c.image}" alt="">` : ''}</span>
                <span class="nani-qa__swatch-name">${escapeHtml(c.name)}</span>
              </button>
            `).join('')}
          </div>` : ''}

        ${partner && partnerSizes.length ? `
          <div class="nani-qa__bundle-sizing-head">${escapeHtml(partner.title)} · size</div>
          <div class="nani-qa__bundle-sizes">
            ${partnerSizes.map((s) => `
              <button type="button" class="nani-qa__size${state.bundleSize === s.label ? ' is-on' : ''}${!s.available ? ' is-out' : ''}"
                      data-set-bundle-size="${escapeHtml(s.label)}"
                      aria-pressed="${state.bundleSize === s.label ? 'true' : 'false'}"
                      ${!s.available ? 'disabled' : ''}>${escapeHtml(s.label)}</button>
            `).join('')}
          </div>` : ''}

        ${partner && bundlePromoOn ? renderBundlePriceline(top, partner) : ''}
      </div>
    `;
  }

  function renderBundlePriceline(top, partner) {
    const totalRaw = top.price + partner.price;
    const totalNow = Math.round(totalRaw * (1 - config.discount / 100));
    return `
      <div class="nani-qa__bundle-priceline">
        <div class="nani-qa__bundle-priceline-left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 12V8H4v4"/><path d="M2 12h20v8H2z"/><path d="M12 4v8"/></svg>
          <div>
            <b>Bundle saving</b>
            <span>${config.discount}% off — applied at checkout</span>
          </div>
        </div>
        <div class="nani-qa__bundle-priceline-num">
          <em>You save ${formatMoney(totalRaw - totalNow)}</em>
          <s>${formatMoney(totalRaw)}</s>
          <b>${formatMoney(totalNow)}</b>
        </div>
      </div>`;
  }

  function renderFooter(product, partner) {
    const shipline = drawerFoot.querySelector('[data-qa-shipline]');
    const shipbar = drawerFoot.querySelector('[data-qa-shipbar]');
    const ctaLabel = drawerFoot.querySelector('[data-qa-cta-label]');
    const ctaTotal = drawerFoot.querySelector('[data-qa-cta-total]');
    const cta = drawerFoot.querySelector('[data-qa-add]');

    const totalRawCents = product.price + (partner ? partner.price : 0);
    const totalCents = partner && bundlePromoOn
      ? Math.round(totalRawCents * (1 - config.discount / 100))
      : totalRawCents;
    const thresholdCents = config.threshold * 100;
    const remaining = Math.max(0, thresholdCents - totalCents);
    const pct = Math.min(100, Math.round((totalCents / thresholdCents) * 100));

    if (shipline) {
      shipline.innerHTML = remaining > 0
        ? `<span>Add <b style="color:var(--nani-summit,currentColor)">${formatMoney(remaining)}</b> more for free shipping</span><span style="font-variant-numeric:tabular-nums">${formatMoney(totalCents)} / $${config.threshold}</span>`
        : `<span><b>You've unlocked free shipping ✓</b></span><span style="font-variant-numeric:tabular-nums">${formatMoney(totalCents)}</span>`;
    }
    if (shipbar) shipbar.style.width = pct + '%';

    const canAdd = !!state.size && (!partner || !!state.bundleSize);
    if (cta) cta.toggleAttribute('disabled', !canAdd);
    if (ctaLabel) ctaLabel.textContent = partner ? 'Add bundle to bag' : 'Add to bag';
    if (ctaTotal) {
      ctaTotal.innerHTML = partner && bundlePromoOn
        ? `<s>${formatMoney(totalRawCents)}</s>${formatMoney(totalCents)}`
        : formatMoney(totalCents);
    }

    if (cta) {
      cta.onclick = () => doAddToCart(product, partner);
    }
  }

  function doAddToCart(product, partner) {
    if (!state.size) return;
    const colorIdx = findOptionIdx(product, 'color');
    const sizeIdx = findOptionIdx(product, 'size');
    const colors = getOptionValues(product, colorIdx);
    const activeColor = colors[state.variantIdx];
    const opts = product.options.map((_, i) => {
      if (i === colorIdx) return activeColor && activeColor.name;
      if (i === sizeIdx) return state.size;
      return product.variants[0].options[i];
    });
    const variant = findVariant(product, opts);

    const items = [];
    if (variant) items.push({ id: variant.id, quantity: 1 });

    if (partner && state.bundleSize) {
      const pColorIdx = findOptionIdx(partner, 'color');
      const pSizeIdx = findOptionIdx(partner, 'size');
      const pColors = getOptionValues(partner, pColorIdx);
      const pColor = pColors[state.bundleVariantIdx];
      const pOpts = partner.options.map((_, i) => {
        if (i === pColorIdx) return pColor && pColor.name;
        if (i === pSizeIdx) return state.bundleSize;
        return partner.variants[0].options[i];
      });
      const pVariant = findVariant(partner, pOpts);
      if (pVariant) {
        items.push(bundlePromoOn
          ? { id: pVariant.id, quantity: 1, properties: { _bundle: 'top-bottom-' + config.discount + 'pct' } }
          : { id: pVariant.id, quantity: 1 });
      }
    }

    if (items.length === 0) return;

    track('quick_add_add_to_cart', {
      product_handle: product.handle,
      bundled: !!(partner && state.bundleSize),
      item_count: items.length,
      collection: window.location.pathname,
    });

    const drawerSectionId = document.querySelector('cart-items-component[data-drawer]')?.dataset.sectionId;
    const url = drawerSectionId
      ? '/cart/add.js?sections=' + encodeURIComponent(drawerSectionId)
      : '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json())
      .then((payload) => {
        // Tell Horizon's cart-items-component to morph itself with the new state.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: payload,
            sourceId: 'nani-collection-quick-add',
            data: {
              source: 'nani-collection-quick-add',
              itemCount: items.reduce((n, it) => n + (it.quantity || 1), 0),
              sections: payload.sections,
            },
          },
        }));
        announce('Added to bag');
        closeDrawer();
      })
      .catch(() => {});
  }

  function renderRating(product) {
    if (state.rating == null && state.ratingCount == null) return '';
    const filled = state.rating != null ? Math.round(Math.max(0, Math.min(5, state.rating))) : 5;
    const stars = '★'.repeat(filled) + '☆'.repeat(5 - filled);
    const url = product && product.url ? (product.url + '#reviews') : '#reviews';
    const countLabel = state.ratingCount != null
      ? `<a href="${url}" class="nani-qa__product-rating-link">${state.ratingCount.toLocaleString()} review${state.ratingCount === 1 ? '' : 's'} →</a>`
      : '';
    return `
      <div class="nani-qa__product-rating">
        <span class="stars" aria-hidden="true">${stars}</span>
        ${state.rating != null ? `<span><b>${state.rating}</b></span>` : ''}
        ${countLabel ? `<span class="dot" aria-hidden="true">·</span>${countLabel}` : ''}
      </div>`;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
})();
